import { yupResolver } from "@hookform/resolvers/yup";
import { inventoryValidationSchema } from "../../utils/validations/validations";
import { InventoryEntryDetails } from "../../interfaces/app_types";
import { Controller, useForm } from "react-hook-form";
import InputSearchableDropdown from "./InputSearchableDropdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addInventory,
  editInventory,
  getLocation,
  getParties,
} from "../../api/inventory";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  invResetFieldsState,
  inventoryModalTitleState,
  toastBgState,
  toastMessageState,
  toastShowState,
} from "../../utils/atoms/atoms";
import { closeModal } from "../../utils/funcs/common";
import { useEffect, useState } from "react";
import LoadingComponent from "../../LoadingComponent";
import FormField from "../common/FormField";

const InventoryFormBody = () => {
  const resolver = yupResolver(inventoryValidationSchema);
  const { data: inventoryItem } = useQuery<any>(["inventory-item"]);

  const part_name = inventoryItem?.part_name;
  const drawing = inventoryItem?.drawing;
  const location = inventoryItem?.location;
  const parties = inventoryItem?.parties;
  const product_line = inventoryItem?.product_line;
  const incoming_remark = inventoryItem?.incoming_remark;
  const per_piece_weight = inventoryItem?.per_piece_weight;
  const plating = inventoryItem?.plating;
  const powder_coating = inventoryItem?.powder_coating;
  const discontinued = inventoryItem?.discontinued;
  const drawing_link = inventoryItem?.drawing_link;
  const inprocess_link = inventoryItem?.inprocess_link;
  const safety_stock = inventoryItem?.safety_stock;
  const laser_cutting = inventoryItem?.laser_cutting;
  const notes = inventoryItem?.notes;

  const defaultValues = {
    part_name: part_name || "",
    drawing: drawing || "",
    location: location || null,
    parties: parties || [],
    product_line: product_line || "",
    incoming_remark: incoming_remark || "",
    per_piece_weight: per_piece_weight || 0,
    plating: plating || false,
    powder_coating: powder_coating || false,
    discontinued: discontinued || false,
    drawing_link: drawing_link || "",
    inprocess_link: inprocess_link || "",
    safety_stock: safety_stock || 0,
    laser_cutting: laser_cutting || false,
    notes: notes || "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<InventoryEntryDetails>({
    resolver,
    values: inventoryItem ? defaultValues : undefined,
  });

  const [invResetFields, setInvResetFields] =
    useRecoilState(invResetFieldsState);
  const inventoryModalTitle = useRecoilValue(inventoryModalTitleState);
  const setToastShow = useSetRecoilState(toastShowState);
  const setToastBg = useSetRecoilState(toastBgState);
  const setToastMessage = useSetRecoilState(toastMessageState);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const isEditForm = inventoryModalTitle === "Edit Inventory";

  const queryClient = useQueryClient();

  const {
    isLoading,
    data: partiesOptions,
    isError,
    isSuccess,
  } = useQuery({
    queryKey: ["parties"],
    queryFn: getParties,
  });

  const { data: locations } = useQuery({
    queryKey: ["location"],
    queryFn: getLocation,
  });

  const addInventoryMutation = useMutation({
    mutationFn: async (data: any) => {
      return addInventory(data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventory-vendor"]);
      closeModal();
      setToastShow(true);
      setIsSubmitSuccessful(true);
      setToastBg("success");
      setToastMessage("Part added");
    },
  });

  const editInventoryMutation = useMutation({
    mutationFn: async (data: any) => {
      return editInventory(inventoryItem.part_id, data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["inventory"]);
      queryClient.invalidateQueries(["inventory-vendor"]);
      closeModal();
      setToastShow(true);
      setIsSubmitSuccessful(true);
      setToastBg("success");
      setToastMessage("Part edited");
    },
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      setIsSubmitSuccessful(false);
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (invResetFields) {
      reset(defaultValues);
      setInvResetFields(false);
    }
  }, [invResetFields]);
  return (
    <LoadingComponent
      isError={isError}
      isSuccess={isSuccess}
      isLoading={isLoading}
      ComponentBody={
        <div>
          <form
            action=""
            onSubmit={handleSubmit((data, event) => {
              event?.preventDefault();
              if (isEditForm) {
                editInventoryMutation.mutate(data);
              } else {
                addInventoryMutation.mutate(data);
              }
            })}
          >
            {isEditForm ? (
              <FormField
                title="Part ID"
                value={inventoryItem?.part_id}
                fieldType={"display"}
              />
            ) : (
              ""
            )}
            <div className="d-flex flex-wrap">
              <FormField
                title="Part Name"
                value={part_name}
                formRegister={{ ...register("part_name") }}
                errorMessage={errors?.part_name?.message}
              />
              <FormField
                title="Drawing"
                value={drawing}
                formRegister={{ ...register("drawing") }}
                errorMessage={errors?.drawing?.message}
              />
              <FormField
                title="Drawing Link"
                value={drawing_link}
                fieldType="url"
                formRegister={{ ...register("drawing_link") }}
                errorMessage={errors?.drawing_link?.message}
              />
              <FormField
                title="Inprocess Link"
                value={inprocess_link}
                fieldType="url"
                formRegister={{ ...register("inprocess_link") }}
                errorMessage={errors?.inprocess_link?.message}
              />
              <FormField
                title="Safety Stock"
                value={safety_stock}
                fieldType="number"
                formRegister={{ ...register("safety_stock") }}
                errorMessage={errors?.safety_stock?.message}
              />
              <FormField
                title="Product Line"
                value={product_line}
                formRegister={{ ...register("product_line") }}
                errorMessage={errors?.product_line?.message}
              />
              <FormField
                title="Incoming Remarks"
                value={incoming_remark}
                formRegister={{ ...register("incoming_remark") }}
                errorMessage={errors?.incoming_remark?.message}
              />
              <FormField
                title="Per Piece Weight"
                value={per_piece_weight}
                fieldType="number"
                step={0.01}
                formRegister={{ ...register("per_piece_weight") }}
                errorMessage={errors?.per_piece_weight?.message}
              />
            </div>
            <div className="d-flex flex-wrap gap-1">
              <Controller
                name="laser_cutting"
                control={control}
                render={({ field: { value } }) => (
                  <FormField
                    title="Laser Cutting"
                    value={value}
                    fieldType="checkbox"
                    inputId="laser_cutting"
                    formRegister={{ ...register("laser_cutting") }}
                  />
                )}
              />
              <Controller
                name="powder_coating"
                control={control}
                render={({ field: { value } }) => (
                  <FormField
                    title="Powder Coating"
                    value={value}
                    fieldType="checkbox"
                    inputId="powder_coating"
                    formRegister={{ ...register("powder_coating") }}
                  />
                )}
              />
              <Controller
                name="plating"
                control={control}
                render={({ field: { value } }) => (
                  <FormField
                    title="Plating"
                    value={value}
                    fieldType="checkbox"
                    inputId="plating"
                    formRegister={{ ...register("plating") }}
                  />
                )}
              />
              <Controller
                name="discontinued"
                control={control}
                render={({ field: { value } }) => (
                  <FormField
                    title="Discontinued"
                    value={value}
                    fieldType="checkbox"
                    inputId="discontinued"
                    formRegister={{ ...register("discontinued") }}
                  />
                )}
              />
            </div>
            <InputSearchableDropdown
              name="location"
              selectedValue={location}
              isColor={true}
              options={locations}
              control={control}
              isAsync={true}
              errorMessage={errors?.location?.message}
            />
            <InputSearchableDropdown
              name="parties"
              selectedValue={parties}
              isColor={true}
              options={partiesOptions}
              control={control}
              isAsync={true}
              isMulti={true}
              errorMessage={errors?.parties?.message}
            />
            <div className="mt-2">
              <FormField
                title="Notes"
                value={notes}
                fieldType="textarea"
                fullWidth={true}
                inputId={"notes"}
                formRegister={{ ...register("notes") }}
                errorMessage={errors?.notes?.message}
              />
            </div>

            <button type="submit" className="styled-btn--dark--bold mt-3">
              Submit
            </button>
          </form>
        </div>
      }
    />
  );
};

export default InventoryFormBody;
